/**
 * Estimates List Page - T180
 *
 * List and filter cost estimates
 */
import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { ProtectedRoute } from '../components/ProtectedRoute';
import { AppShell } from '../components/layout/AppShell';
import { GlassPanel, Button, Input, LoadingSpinner, Pagination, EmptyState } from '../components/ui';
import { EstimateCard } from '../components/estimates/EstimateCard';
import { useEstimates, statusLabels, type EstimateStatus } from '../hooks/useEstimates';

export const Route = createFileRoute('/estimates')({
  component: EstimatesPage,
});

const statusOptions: (EstimateStatus | 'all')[] = ['all', 'draft', 'sent', 'accepted', 'rejected', 'expired', 'converted'];

function EstimatesPage() {
  const [status, setStatus] = useState<EstimateStatus | undefined>(undefined);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const { data, isLoading } = useEstimates({
    page,
    limit: 40,
    status,
    search: search || undefined,
    sortBy: 'issueDate',
    sortOrder: 'desc',
  });

  const estimates = data?.estimates || [];
  const pagination = {
    page: data?.page || 1,
    totalPages: data?.totalPages || 1,
  };

  return (
    <ProtectedRoute>
      <AppShell>
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-white">Angebote</h1>
            <p className="text-white/50 mt-1">
              {data?.total || 0} Angebot{data?.total !== 1 ? 'e' : ''}
            </p>
          </div>
          <Link to="/estimates/new">
            <Button>+ Neues Angebot</Button>
          </Link>
        </div>

        {/* Filters */}
        <GlassPanel className="mb-6">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Status Pills */}
            <div className="flex flex-wrap gap-2">
              {statusOptions.map((s) => (
                <button
                  key={s}
                  onClick={() => {
                    setStatus(s === 'all' ? undefined : s);
                    setPage(1);
                  }}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                    (s === 'all' && !status) || s === status
                      ? 'bg-primary-500 text-white'
                      : 'bg-white/10 text-white/70 hover:bg-white/20'
                  }`}
                >
                  {s === 'all' ? 'Alle' : statusLabels[s]}
                </button>
              ))}
            </div>

            {/* Search */}
            <div className="flex-1">
              <Input
                type="search"
                placeholder="Suchen nach Nummer, Betreff..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="w-full"
              />
            </div>
          </div>
        </GlassPanel>

        {/* Content */}
        {isLoading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        ) : estimates.length === 0 ? (
          <EmptyState
            title="Keine Angebote gefunden"
            description={
              search || status
                ? 'Versuchen Sie, Ihre Filter anzupassen'
                : 'Erstellen Sie Ihr erstes Angebot'
            }
          />
        ) : (
          <>
            {/* Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              {estimates.map((estimate) => (
                <EstimateCard key={estimate.id} estimate={estimate} />
              ))}
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <Pagination
                page={pagination.page}
                totalPages={pagination.totalPages}
                onPageChange={setPage}
              />
            )}
          </>
        )}
      </AppShell>
    </ProtectedRoute>
  );
}
